import { api } from './api';

// Types
export type ConventionStatus = 'NUEVO' | 'EN_PROCESO' | 'VIGENTE' | 'POR_RENOVAR' | 'EN_GESTION_CONTACTOS' | 'ARCHIVED';

export type PhaseStatus = 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE';

export interface Phase {
  id: string;
  phaseNumber: number;
  name: string;
  description: string | null;
  status: PhaseStatus;
  startDate: string | null;
  dueDate: string | null;
  endDate: string | null;
  completedAt: string | null;
  observations: string | null;
  isCurrent: boolean;
  conventionId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    files: number;
    reminders: number;
  };
}

export interface Contact {
  id: string;
  businessName: string;
  contactName: string | null;
  ruc: string | null;
  rubro: string | null;
  district: string | null;
  phone: string | null;
  email: string | null;
}

export interface Convention {
  id: string;
  code: string | null;
  institutionName: string | null;
  title: string | null;
  description: string | null;
  status: ConventionStatus;
  startDate: string | null;
  endDate: string | null;
  signatureDate: string | null;
  renewedAt: string | null;
  managerUcv: string | null;
  observations: string | null;
  ruc: string | null;
  businessType: string | null;
  district: string | null;
  logo: string | null;
  contactId: string;
  contact: Contact;
  sourceSheet: string | null;
  sourceRow: number | null;
  isImported: boolean;
  driveFolderId: string | null;
  conventionMetadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
  phases?: Phase[];
  history?: HistoryEntry[];
  _count?: {
    phases: number;
    files: number;
  };
}

export interface HistoryEntry {
  id: string;
  action: string;
  description: string | null;
  metadata: Record<string, unknown> | null;
  userId: string | null;
  user: { id: string; name: string; email: string } | null;
  createdAt: string;
}

export interface CreateConventionDTO {
  code?: string | null;
  institutionName: string;
  ruc?: string | null;
  businessType?: string | null;
  district?: string | null;
  observations?: string | null;
  contactId?: string | null;
  status?: ConventionStatus;
}

export interface UpdateConventionDTO {
  code?: string | null;
  institutionName?: string;
  title?: string;
  description?: string;
  ruc?: string | null;
  businessType?: string | null;
  district?: string | null;
  observations?: string | null;
  managerUcv?: string | null;
  contactId?: string | null;
  status?: ConventionStatus;
}

export interface ConventionFilters {
  search?: string;
  status?: string;
  rubro?: string;
  district?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  limit?: number;
  include?: string;
  isImported?: string;
}

export interface ConventionListResponse {
  data: Convention[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface ConventionStats {
  total: number;
  byStatus: Record<string, number>;
  byBusinessType: Array<{ rubro: string; count: number }>;
  overduePhases: Array<{
    phaseId: string;
    phaseNumber: number;
    phaseName: string;
    dueDate: string;
    conventionId: string;
    conventionName: string;
    conventionCode: string;
    daysOverdue: number;
  }>;
  upcomingPhases: Array<{
    phaseId: string;
    phaseNumber: number;
    phaseName: string;
    dueDate: string;
    conventionId: string;
    conventionName: string;
    conventionCode: string;
    daysRemaining: number;
  }>;
}

export const conventionService = {
  async getConventions(filters: ConventionFilters = {}): Promise<ConventionListResponse> {
    const params = new URLSearchParams();
    if (filters.search) {params.set('search', filters.search);}
    if (filters.status) {params.set('status', filters.status);}
    if (filters.rubro) {params.set('rubro', filters.rubro);}
    if (filters.district) {params.set('district', filters.district);}
    if (filters.dateFrom) {params.set('dateFrom', filters.dateFrom);}
    if (filters.dateTo) {params.set('dateTo', filters.dateTo);}
    if (filters.page) {params.set('page', String(filters.page));}
    if (filters.limit) {params.set('limit', String(filters.limit));}
    if (filters.include) {params.set('include', filters.include);}
    if (filters.isImported) {params.set('isImported', filters.isImported);}

    const response = await api.get<ConventionListResponse>(`/conventions?${params.toString()}`);
    return response.data;
  },

  async getConvention(id: string): Promise<Convention> {
    const response = await api.get<Convention>(`/conventions/${id}`);
    return response.data;
  },

  async getConventionStats(): Promise<ConventionStats> {
    const response = await api.get<ConventionStats>('/conventions/stats');
    return response.data;
  },

  async createConvention(data: CreateConventionDTO): Promise<Convention> {
    const response = await api.post<Convention>('/conventions', data);
    return response.data;
  },

  async updateConvention(id: string, data: UpdateConventionDTO): Promise<Convention> {
    const response = await api.put<Convention>(`/conventions/${id}`, data);
    return response.data;
  },

  async deleteConvention(id: string): Promise<void> {
    await api.delete(`/conventions/${id}`);
  },

  // Phase actions
  async getPhases(conventionId: string): Promise<Phase[]> {
    const response = await api.get<Phase[]>(`/conventions/${conventionId}/phases`);
    return response.data;
  },

  async startPhase(conventionId: string, phaseNumber: number): Promise<Phase> {
    const response = await api.patch<Phase>(
      `/conventions/${conventionId}/phases/${phaseNumber}/start`
    );
    return response.data;
  },

  async completePhase(conventionId: string, phaseNumber: number, signatureDate?: string): Promise<Phase> {
    const response = await api.patch<Phase>(
      `/conventions/${conventionId}/phases/${phaseNumber}/complete`,
      signatureDate ? { signatureDate } : undefined
    );
    return response.data;
  },

  // Sprint 4: Phase Actions
  async reschedulePhase(conventionId: string, phaseNumber: number, startDate: string): Promise<Phase> {
    const response = await api.patch<Phase>(
      `/conventions/${conventionId}/phases/${phaseNumber}/reschedule`,
      { startDate }
    );
    return response.data;
  },

  async skipPhase(conventionId: string, phaseNumber: number): Promise<Phase> {
    const response = await api.patch<Phase>(
      `/conventions/${conventionId}/phases/${phaseNumber}/skip`
    );
    return response.data;
  },

  async getOverduePhases(): Promise<{ data: any[]; total: number }> {
    const response = await api.get('/phases/overdue');
    return response.data;
  },

  async getUpcomingPhases(): Promise<{ data: any[]; total: number }> {
    const response = await api.get('/phases/upcoming');
    return response.data;
  },

  async checkOverduePhases(): Promise<any> {
    const response = await api.post('/phases/check-overdue');
    return response.data;
  },

  // Fase 1: Archive / Unarchive
  async archiveConvention(id: string): Promise<Convention> {
    const response = await api.patch<Convention>(`/conventions/${id}/archive`);
    return response.data;
  },

  async unarchiveConvention(id: string): Promise<Convention> {
    const response = await api.patch<Convention>(`/conventions/${id}/unarchive`);
    return response.data;
  },

  // Fase 3: Drive Folder
  async createDriveFolder(id: string): Promise<Convention> {
    const response = await api.post<Convention>(`/conventions/${id}/drive-folder`);
    return response.data;
  },

  async exportConventions(startDate?: string, endDate?: string, signatureDateFrom?: string, signatureDateTo?: string): Promise<void> {
    const params = new URLSearchParams();
    if (startDate) {params.set('startDate', startDate);}
    if (endDate) {params.set('endDate', endDate);}
    if (signatureDateFrom) {params.set('signatureDateFrom', signatureDateFrom);}
    if (signatureDateTo) {params.set('signatureDateTo', signatureDateTo);}

    const response = await api.get(`/conventions/export?${params.toString()}`, {
      responseType: 'blob',
    });

    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'convenios.xlsx';
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?(.+?)"?$/);
      if (match) {
        filename = match[1];
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  async updateMetadata(id: string, metadata: Record<string, any>): Promise<Convention> {
    const response = await api.patch<Convention>(`/conventions/${id}/metadata`, { metadata });
    return response.data;
  },
};
